import { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router'

export interface NavigationState {
  isMobileMenuOpen: boolean
  isSidebarCollapsed: boolean
  activeSection: string | null
  breadcrumbs: Array<{
    label: string
    href: string
    isActive: boolean
  }>
}

export interface ResponsiveNavigationConfig {
  mobileBreakpoint: number
  tabletBreakpoint: number
  autoCloseOnRouteChange: boolean
  collapseSidebarOnMobile: boolean
  enableBreadcrumbs: boolean
}

const defaultConfig: ResponsiveNavigationConfig = {
  mobileBreakpoint: 768,
  tabletBreakpoint: 1024,
  autoCloseOnRouteChange: true,
  collapseSidebarOnMobile: true,
  enableBreadcrumbs: true
}

export function useResponsiveNavigation(config: Partial<ResponsiveNavigationConfig> = {}) {
  const mergedConfig = { ...defaultConfig, ...config }
  const location = useLocation()
  
  const [navigationState, setNavigationState] = useState<NavigationState>({
    isMobileMenuOpen: false,
    isSidebarCollapsed: false,
    activeSection: null,
    breadcrumbs: []
  })

  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768
  })

  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>('desktop')

  // Update screen size and device type
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      const height = window.innerHeight
      
      setScreenSize({ width, height })
      
      // Determine device type
      if (width < mergedConfig.mobileBreakpoint) {
        setDeviceType('mobile')
      } else if (width < mergedConfig.tabletBreakpoint) {
        setDeviceType('tablet')
      } else {
        setDeviceType('desktop')
      }
    }

    handleResize() // Initial call
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [mergedConfig.mobileBreakpoint, mergedConfig.tabletBreakpoint])

  // Auto-close mobile menu on route change
  useEffect(() => {
    if (mergedConfig.autoCloseOnRouteChange && navigationState.isMobileMenuOpen) {
      setNavigationState(prev => ({
        ...prev,
        isMobileMenuOpen: false
      }))
    }
  }, [location.pathname, mergedConfig.autoCloseOnRouteChange, navigationState.isMobileMenuOpen])

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    if (mergedConfig.collapseSidebarOnMobile && deviceType === 'mobile') {
      setNavigationState(prev => ({
        ...prev,
        isSidebarCollapsed: true
      }))
    }
  }, [deviceType, mergedConfig.collapseSidebarOnMobile])

  // Generate breadcrumbs from current path
  useEffect(() => {
    if (mergedConfig.enableBreadcrumbs) {
      const pathSegments = location.pathname.split('/').filter(Boolean)
      const breadcrumbs = pathSegments.map((segment, index) => {
        const href = '/' + pathSegments.slice(0, index + 1).join('/')
        const isActive = index === pathSegments.length - 1
        
        return {
          label: segment
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' '),
          href,
          isActive
        }
      })

      setNavigationState(prev => ({
        ...prev,
        breadcrumbs
      }))
    }
  }, [location.pathname, mergedConfig.enableBreadcrumbs])

  // Navigation actions
  const toggleMobileMenu = useCallback(() => {
    setNavigationState(prev => ({
      ...prev,
      isMobileMenuOpen: !prev.isMobileMenuOpen
    }))
  }, [])

  const closeMobileMenu = useCallback(() => {
    setNavigationState(prev => ({
      ...prev,
      isMobileMenuOpen: false
    }))
  }, [])

  const openMobileMenu = useCallback(() => {
    setNavigationState(prev => ({
      ...prev,
      isMobileMenuOpen: true
    }))
  }, [])

  const toggleSidebar = useCallback(() => {
    setNavigationState(prev => ({
      ...prev,
      isSidebarCollapsed: !prev.isSidebarCollapsed
    }))
  }, [])

  const collapseSidebar = useCallback(() => {
    setNavigationState(prev => ({
      ...prev,
      isSidebarCollapsed: true
    }))
  }, [])

  const expandSidebar = useCallback(() => {
    setNavigationState(prev => ({
      ...prev,
      isSidebarCollapsed: false
    }))
  }, [])

  const setActiveSection = useCallback((section: string | null) => {
    setNavigationState(prev => ({
      ...prev,
      activeSection: section
    }))
  }, [])

  // Responsive utilities
  const isMobile = deviceType === 'mobile'
  const isTablet = deviceType === 'tablet'
  const isDesktop = deviceType === 'desktop'
  
  const shouldShowMobileMenu = isMobile || isTablet
  const shouldShowSidebar = isDesktop || (!isMobile && !navigationState.isSidebarCollapsed)
  const shouldShowBreadcrumbs = mergedConfig.enableBreadcrumbs && (isTablet || isDesktop)

  // Keyboard navigation
  const handleKeyboardNavigation = useCallback((event: KeyboardEvent) => {
    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case 'b':
          event.preventDefault()
          if (shouldShowMobileMenu) {
            toggleMobileMenu()
          } else {
            toggleSidebar()
          }
          break
        case 'm':
          event.preventDefault()
          if (shouldShowMobileMenu) {
            toggleMobileMenu()
          }
          break
      }
    }
  }, [shouldShowMobileMenu, toggleMobileMenu, toggleSidebar])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardNavigation)
    return () => document.removeEventListener('keydown', handleKeyboardNavigation)
  }, [handleKeyboardNavigation])

  return {
    // State
    navigationState,
    screenSize,
    deviceType,
    
    // Device detection
    isMobile,
    isTablet,
    isDesktop,
    
    // Responsive flags
    shouldShowMobileMenu,
    shouldShowSidebar,
    shouldShowBreadcrumbs,
    
    // Actions
    toggleMobileMenu,
    closeMobileMenu,
    openMobileMenu,
    toggleSidebar,
    collapseSidebar,
    expandSidebar,
    setActiveSection,
    
    // Utilities
    handleKeyboardNavigation
  }
}

// Hook for navigation state management
export function useNavigationState() {
  const [navigationHistory, setNavigationHistory] = useState<string[]>([])
  const [navigationDepth, setNavigationDepth] = useState(0)

  const addToHistory = useCallback((path: string) => {
    setNavigationHistory(prev => {
      const newHistory = [...prev, path]
      // Keep only last 10 entries
      return newHistory.slice(-10)
    })
    setNavigationDepth(prev => prev + 1)
  }, [])

  const goBack = useCallback(() => {
    if (navigationHistory.length > 0) {
      setNavigationHistory(prev => prev.slice(0, -1))
      setNavigationDepth(prev => Math.max(0, prev - 1))
    }
  }, [navigationHistory.length])

  const canGoBack = navigationHistory.length > 0

  return {
    navigationHistory,
    navigationDepth,
    addToHistory,
    goBack,
    canGoBack
  }
}

// Hook for navigation analytics
export function useNavigationAnalytics() {
  const [navigationMetrics, setNavigationMetrics] = useState({
    totalNavigations: 0,
    averageNavigationTime: 0,
    mostVisitedSections: [] as Array<{ section: string; count: number }>,
    navigationErrors: 0
  })

  const trackNavigation = useCallback((section: string, duration?: number) => {
    setNavigationMetrics(prev => ({
      ...prev,
      totalNavigations: prev.totalNavigations + 1,
      averageNavigationTime: duration 
        ? (prev.averageNavigationTime + duration) / 2 
        : prev.averageNavigationTime,
      mostVisitedSections: (() => {
        const existing = prev.mostVisitedSections.find(item => item.section === section)
        if (existing) {
          return prev.mostVisitedSections.map(item =>
            item.section === section 
              ? { ...item, count: item.count + 1 }
              : item
          )
        } else {
          return [...prev.mostVisitedSections, { section, count: 1 }]
        }
      })()
    }))
  }, [])

  const trackNavigationError = useCallback(() => {
    setNavigationMetrics(prev => ({
      ...prev,
      navigationErrors: prev.navigationErrors + 1
    }))
  }, [])

  return {
    navigationMetrics,
    trackNavigation,
    trackNavigationError
  }
}
